import { RootStackParamList } from '../../app/navigator';

export type RememberedTable = Pick<
	RootStackParamList['Game'],
	'tableId' | 'legacyRoomId' | 'roomSlug' | 'tableName' | 'isTournamentRoom'
>;

type StoredTable = RememberedTable & { token: string };

const KEY = 'svara_open_table';

/**
 * The table the player has open, kept in sessionStorage so it survives a page reload (F5) and
 * nothing else: closing the tab or the native app forgets it. Web only; native has no
 * sessionStorage and never reloads under a seated player.
 */
const storage: Storage | null = (() => {
	try {
		return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
	} catch {
		return null;
	}
})();

const read = (): StoredTable | null => {
	try {
		const raw = storage?.getItem(KEY);
		return raw ? (JSON.parse(raw) as StoredTable) : null;
	} catch {
		return null;
	}
};

/** Records the open table; returns the token that lets only this screen forget it again. */
export const rememberOpenTable = (table: RememberedTable): string => {
	const token = `${table.tableId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
	try {
		storage?.setItem(KEY, JSON.stringify({ ...table, token }));
	} catch {
		// Storage full or blocked: a reload then just lands on Home, as before.
	}
	return token;
};

/**
 * Forgets the open table when its screen goes away. A table switch mounts the next Game screen
 * before the old one unmounts, so the old screen must not wipe the new screen's entry.
 */
export const forgetOpenTable = (token: string) => {
	if (read()?.token !== token) return;
	try {
		storage?.removeItem(KEY);
	} catch {
		// ignore
	}
};

/** The table that was open when the page was last unloaded, if any, with its forget token. */
export const readOpenTableBeforeReload = (): StoredTable | null => {
	const stored = read();
	return stored?.tableId ? stored : null;
};
